/**
 * TutorialRewardModal — reward reveal shown when a tutorial module is cleared.
 * Pops a die thumbnail with an "unlocked" flourish and an optional Stacks line,
 * then a single continue button. Body-level singleton, reusable across both
 * modules (and later the tasks list).
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { sound, SoundId } from '../../engine/audio/sound';

interface TutorialRewardEvents {
  /** Continue button tapped. */
  'continue': void;
}

export interface TutorialRewardOptions {
  /** Big heading, e.g. "NICE ROLL!" */
  heading: string;
  /** Body line for a message-only (congrats) beat — shown when there's no die. */
  message?: string;
  /** Thumbnail URL for the unlocked die (omit for a message-only beat). */
  dieThumbnailUrl?: string;
  /** Die label under the thumbnail, e.g. the dice code. */
  dieLabel?: string;
  /** Optional Stacks reward to show as a "+100M" pill. */
  stacks?: number;
  /** Continue button label. Default "CONTINUE". */
  buttonLabel?: string;
}

const STYLE_ID = 'sdl-treward-styles';

export class TutorialRewardModal extends EventEmitter<TutorialRewardEvents> {
  public element: HTMLElement;
  private panelEl: HTMLElement;
  private headingEl: HTMLElement;
  private messageEl: HTMLElement;
  private dieWrapEl: HTMLElement;
  private confettiEl: HTMLElement;
  private thumbEl: HTMLImageElement;
  private labelEl: HTMLElement;
  private stacksEl: HTMLElement;
  private btnEl: HTMLElement;
  private visible = false;

  constructor() {
    super();
    TutorialRewardModal.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-treward-host';
    this.element.innerHTML = `
      <div class="sdl-treward-backdrop"></div>
      <div class="sdl-treward-confetti" aria-hidden="true"></div>
      <div class="sdl-treward-panel">
        <div class="sdl-treward-heading"></div>
        <div class="sdl-treward-message"></div>
        <div class="sdl-treward-diewrap">
          <img class="sdl-treward-thumb" alt="Unlocked dice" />
          <div class="sdl-treward-glow"></div>
        </div>
        <div class="sdl-treward-label"></div>
        <div class="sdl-treward-stacks"></div>
        <button class="sdl-treward-btn" data-continue>CONTINUE</button>
      </div>
    `;
    document.body.appendChild(this.element);

    this.panelEl = this.element.querySelector('.sdl-treward-panel') as HTMLElement;
    this.headingEl = this.element.querySelector('.sdl-treward-heading') as HTMLElement;
    this.messageEl = this.element.querySelector('.sdl-treward-message') as HTMLElement;
    this.dieWrapEl = this.element.querySelector('.sdl-treward-diewrap') as HTMLElement;
    this.confettiEl = this.element.querySelector('.sdl-treward-confetti') as HTMLElement;
    this.thumbEl = this.element.querySelector('.sdl-treward-thumb') as HTMLImageElement;
    this.labelEl = this.element.querySelector('.sdl-treward-label') as HTMLElement;
    this.stacksEl = this.element.querySelector('.sdl-treward-stacks') as HTMLElement;
    this.btnEl = this.element.querySelector('[data-continue]') as HTMLElement;

    this.btnEl.addEventListener('click', () => {
      // Hide BEFORE emitting: a 'continue' listener may synchronously re-show
      // this same modal for the next beat (congrats → reward). If we hid after,
      // we'd undo that re-show and the next beat would never appear.
      this.hide();
      this.emit('continue', undefined);
    });
  }

  show(opts: TutorialRewardOptions): void {
    this.headingEl.textContent = opts.heading;

    // Message-only (congrats) vs die-reveal beat.
    if (opts.message) {
      this.messageEl.textContent = opts.message;
      this.messageEl.style.display = 'block';
    } else {
      this.messageEl.style.display = 'none';
    }
    if (opts.dieThumbnailUrl) {
      this.thumbEl.src = opts.dieThumbnailUrl;
      this.labelEl.textContent = opts.dieLabel ?? '';
      this.dieWrapEl.style.display = '';
      this.labelEl.style.display = '';
      // Restart the die spin-in (the modal element is reused across beats).
      this.thumbEl.style.animation = 'none';
      void this.thumbEl.offsetWidth;
      this.thumbEl.style.animation = '';
      // Celebrate the unlock with a confetti burst — the explosion sound rides
      // the burst, the reveal stinger rides the die flipping in.
      this.burstConfetti();
      sound.play(SoundId.CONFETTI_POP);
      sound.play(SoundId.DICE_REVEAL);
    } else {
      this.dieWrapEl.style.display = 'none';
      this.labelEl.style.display = 'none';
      this.confettiEl.innerHTML = '';
      // Message-only success beat (e.g. "CONGRATS!" / "NICE BET!") → reward chime.
      sound.play(SoundId.REWARD_CHIME);
    }

    // Pop the panel in (scale 0.7→1, back-out) on every beat.
    this.panelEl.style.animation = 'none';
    void this.panelEl.offsetWidth;
    this.panelEl.style.animation = '';
    if (opts.stacks && opts.stacks > 0) {
      this.stacksEl.textContent = `+ ${TutorialRewardModal.formatStacks(opts.stacks)} Stacks`;
      this.stacksEl.style.display = 'block';
    } else {
      this.stacksEl.style.display = 'none';
    }
    this.btnEl.textContent = opts.buttonLabel ?? 'CONTINUE';

    if (this.visible) return;
    this.visible = true;
    this.element.classList.add('visible');
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
    this.confettiEl.innerHTML = '';
  }

  /** Spawn a one-shot confetti burst radiating from the panel centre. */
  private burstConfetti(): void {
    const colors = ['#00E5FF', '#FFD75E', '#00FF88', '#FF3DAE', '#FFFFFF'];
    const N = 44;
    let html = '';
    for (let i = 0; i < N; i++) {
      const angle = (i / N) * Math.PI * 2 + Math.random() * 0.5;
      const dist = 120 + Math.random() * 220;
      const tx = Math.cos(angle) * dist;
      const ty = Math.sin(angle) * dist;
      const rot = Math.random() * 720 - 360;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const delay = (Math.random() * 0.08).toFixed(3);
      const round = Math.random() < 0.5 ? '50%' : '2px';
      html += `<i style="--tx:${tx.toFixed(0)}px;--ty:${ty.toFixed(0)}px;--rot:${rot.toFixed(0)}deg;`
        + `background:${color};border-radius:${round};animation-delay:${delay}s"></i>`;
    }
    this.confettiEl.innerHTML = html;
  }

  isVisible(): boolean {
    return this.visible;
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private static formatStacks(n: number): string {
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(n % 1_000_000_000 ? 1 : 0)}B`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return `${n}`;
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-treward-host {
        position: fixed; inset: 0;
        z-index: 820;
        display: none;
        align-items: center; justify-content: center;
      }
      .sdl-treward-host.visible { display: flex; }
      .sdl-treward-backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.78);
        backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
      }
      .sdl-treward-panel {
        position: relative;
        z-index: 2;
        width: 290px;
        padding: 26px 22px 20px;
        background: var(--concrete, #111114);
        border: 1px solid rgba(0,229,255,0.25);
        border-radius: 22px;
        display: flex; flex-direction: column; align-items: center;
        gap: 10px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.65);
        animation: sdl-treward-panel-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      @keyframes sdl-treward-panel-in {
        0% { opacity: 0; transform: scale(0.7); }
        100% { opacity: 1; transform: scale(1); }
      }

      /* Confetti burst */
      .sdl-treward-confetti {
        position: absolute; top: 50%; left: 50%;
        width: 0; height: 0; z-index: 1;
        pointer-events: none;
      }
      .sdl-treward-confetti i {
        position: absolute; top: 0; left: 0;
        width: 9px; height: 9px;
        opacity: 0;
        animation: sdl-treward-confetti 1.15s cubic-bezier(0.15, 0.6, 0.4, 1) forwards;
      }
      @keyframes sdl-treward-confetti {
        0% { opacity: 1; transform: translate(0, 0) rotate(0deg) scale(1); }
        70% { opacity: 1; }
        100% {
          opacity: 0;
          transform: translate(var(--tx), calc(var(--ty) + 70px)) rotate(var(--rot)) scale(0.6);
        }
      }

      .sdl-treward-heading {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 30px; letter-spacing: 2px;
        color: #fff;
      }
      .sdl-treward-message {
        font-family: 'Rajdhani', sans-serif;
        font-size: 15px; line-height: 1.4; text-align: center;
        color: rgba(255,255,255,0.7);
        margin: 2px 4px 4px;
      }
      .sdl-treward-diewrap {
        position: relative;
        width: 140px; height: 140px;
        display: flex; align-items: center; justify-content: center;
        margin: 4px 0;
      }
      .sdl-treward-glow {
        position: absolute; inset: 0;
        z-index: 0; /* behind the die thumbnail (which sits at z-index:1) */
        border-radius: 50%;
        background: radial-gradient(circle, rgba(0,229,255,0.35), transparent 65%);
        animation: sdl-treward-pulse 1.8s ease-in-out infinite;
      }
      @keyframes sdl-treward-pulse {
        0%, 100% { transform: scale(0.92); opacity: 0.7; }
        50% { transform: scale(1.08); opacity: 1; }
      }
      .sdl-treward-thumb {
        position: relative;
        z-index: 1; /* above the radial glow background */
        width: 116px; height: 116px;
        object-fit: contain;
        border-radius: 16px;
        filter: drop-shadow(0 8px 20px rgba(0,0,0,0.5));
        animation: sdl-treward-pop 0.6s cubic-bezier(0.2, 1.3, 0.4, 1) both;
      }
      @keyframes sdl-treward-pop {
        0% { transform: perspective(420px) rotateY(-140deg) scale(0.4); opacity: 0; }
        55% { opacity: 1; }
        100% { transform: perspective(420px) rotateY(0deg) scale(1); opacity: 1; }
      }
      .sdl-treward-label {
        font-family: 'Rajdhani', sans-serif;
        font-size: 13px; letter-spacing: 1px;
        color: rgba(255,255,255,0.5);
      }
      .sdl-treward-stacks {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 22px; letter-spacing: 1px;
        color: #00FF88;
        text-shadow: 0 0 12px rgba(0,255,136,0.4);
      }
      .sdl-treward-btn {
        width: 100%;
        margin-top: 6px;
        padding: 14px 0;
        border: none; border-radius: 14px;
        background: linear-gradient(135deg, #00E5FF, #0091b3);
        color: #042127;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 20px; letter-spacing: 2px;
        cursor: pointer;
        box-shadow: 0 6px 18px rgba(0,229,255,0.28);
        transition: transform 0.1s;
      }
      .sdl-treward-btn:active { transform: scale(0.97); }
    `;
    document.head.appendChild(style);
  }
}
